import { FC, ReactNode } from 'react'
import FormItem from 'antd/es/form/FormItem'
import cn from 'classnames'
import { InputNew } from '@UI/InputNew'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import MonthlyPaymentTable from '@pages/Admin/CourseSettings/components/TariffCard/components/MonthlyPaymentTable/MonthlyPaymentTable'
import { RequestCreateLeadPaymentLink } from '@/api/types/crm/crm.types'
import EnrollDateCheckbox from '../../EnrollDateCheckbox'
import CourseDiscountModeFields from '../../CourseDiscountModeFields'
import type { CrmLinkDiscountMode } from '../../discountMode.utils'
import type { MonthlyCourseLinkPeriodUnit } from '../MonthlyCourseLinkFields/monthlyCourseLink.utils'
import StreamCustomPeriodPeriodFields from './StreamCustomPeriodPeriodFields'
import {
  getStreamCustomPeriodHint,
  STREAM_CUSTOM_PERIOD_PRICE_LOCKED_TOOLTIP,
  STREAM_CUSTOM_PERIOD_UNFIXED_PRICE_HINT,
} from './streamCustomPeriodLink.utils'
import { CREATE_LINK_BLOCK_TARIFF_TYPES } from '../../tariffTypes'
import { getCreateLinkBlockTariffTypesHint } from '../../createLinkBlockInfoContent'
import CreateLinkSectionTitle from '../../CreateLinkSectionTitle'
import CreateLinkNumberInput, {
  createLinkNumberRuleTransform,
} from '../../CreateLinkNumberInput'
import sclasses from '../../../SetCourseClassDirectionModal/index.module.css'
import spclasses from '../../../SetCourseClassDirectionModal/purchase.module.css'

type FieldType = Omit<RequestCreateLeadPaymentLink, 'lead_funnel_id'>

type StreamCustomPeriodLinkFieldsProps = {
  tariff: ICourseTariff
  isPeriodFixed: boolean
  periodUnit: MonthlyCourseLinkPeriodUnit
  periodValue: number
  basePrice: number
  discount: number | null | undefined
  discountMode: CrmLinkDiscountMode | null
  price: number | null | undefined
  suggestedPrice: number
  onTogglePeriod: (checked?: boolean) => void
  onDiscountModeChange: (mode: CrmLinkDiscountMode | null) => void
}

/**
 * UI-блок CRM-ссылки для stream_custom_period / days / weeks.
 * Без фиксации: скидка сверху + first_only/every_payment.
 * С фиксацией: скидка над «Итого» = renewal_only.
 */
const StreamCustomPeriodLinkFields: FC<StreamCustomPeriodLinkFieldsProps> = ({
  tariff,
  isPeriodFixed,
  periodUnit,
  periodValue,
  basePrice,
  discount,
  discountMode,
  price,
  suggestedPrice,
  onTogglePeriod,
  onDiscountModeChange,
}) => {
  const netPrice = Number(price) || suggestedPrice

  const renewalDiscountSlot: ReactNode = isPeriodFixed ? (
    <FormItem<FieldType> name="discount" noStyle>
      <CourseDiscountModeFields
        variant="renewal_only"
        discountMode={discountMode}
        onDiscountModeChange={onDiscountModeChange}
        fullBasePrice={basePrice}
      />
    </FormItem>
  ) : null

  return (
    <>
      <div className={spclasses.inputWithLabel}>
        <FormItem<FieldType> name="enroll_date" noStyle>
          <EnrollDateCheckbox />
        </FormItem>
      </div>

      <StreamCustomPeriodPeriodFields
        tariff={tariff}
        isPeriodFixed={isPeriodFixed}
        periodUnit={periodUnit}
        periodValue={periodValue}
        onTogglePeriod={onTogglePeriod}
      />

      {!isPeriodFixed ? (
        <FormItem<FieldType> name="discount" noStyle>
          <CourseDiscountModeFields
            variant="first_payment"
            discountMode={discountMode}
            onDiscountModeChange={onDiscountModeChange}
            fullBasePrice={basePrice}
          />
        </FormItem>
      ) : null}

      <FormItem<FieldType> name="discount_mode" hidden>
        <input type="hidden" />
      </FormItem>
      <FormItem<FieldType>
        name="apply_discount_each_payment"
        valuePropName="checked"
        hidden
      >
        <input type="checkbox" readOnly />
      </FormItem>

      <div className={spclasses.inputWithLabel}>
        <CreateLinkSectionTitle
          title="Ценообразование за периоды обучения"
          infoText={getCreateLinkBlockTariffTypesHint(
            CREATE_LINK_BLOCK_TARIFF_TYPES.streamCustomPeriod,
          )}
        />
        <MonthlyPaymentTable tariff={tariff} readOnly />
      </div>

      {renewalDiscountSlot}

      <div className={spclasses.inputWithLabel}>
        {isPeriodFixed ? (
          <>
            <FormItem<FieldType>
              name="price"
              rules={[
                {
                  required: true,
                  message: 'Укажите итоговую стоимость',
                },
                {
                  type: 'number',
                  min: 0,
                  message: 'Цена не может быть отрицательной',
                  transform: createLinkNumberRuleTransform,
                },
              ]}
              noStyle
            >
              <CreateLinkNumberInput
                className={spclasses.input}
                title="Итого к оплате"
                min={0}
                step={0.01}
                placeholder={0}
              />
            </FormItem>
            <div className={sclasses.textDescription}>
              Период зафиксирован: укажите итоговую сумму по таблице
              ценообразования тарифа.
              {Number(discount) > 0
                ? ' Скидка выше применяется только к продлениям.'
                : ''}
            </div>
          </>
        ) : (
          <>
            <div className={spclasses.inputLabel}>Итого к оплате</div>
            <TooltipWrapper
              placement="top"
              title={STREAM_CUSTOM_PERIOD_PRICE_LOCKED_TOOLTIP}
            >
              <span style={{ display: 'block', cursor: 'not-allowed' }}>
                {/* @ts-ignore */}
                <InputNew
                  className={cn(spclasses.input, spclasses.disabledInput)}
                  type="number"
                  min={0}
                  step={0.01}
                  value={netPrice}
                  disabled
                />
                <FormItem<FieldType> name="price" hidden>
                  <input type="hidden" />
                </FormItem>
              </span>
            </TooltipWrapper>
            <div className={sclasses.textDescription}>
              {getStreamCustomPeriodHint(
                periodValue,
                basePrice,
                discount,
                netPrice,
                tariff.type,
              )}
              <div style={{ marginTop: 4 }}>
                {STREAM_CUSTOM_PERIOD_UNFIXED_PRICE_HINT}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  )
}

export default StreamCustomPeriodLinkFields
